// Browsers block non-muted media playback until the page has received a user
// gesture. The webplayer starts videos muted and silences background audio
// until any pointer/touch/key event fires — at that point this hook flips
// `unlocked` and the audio surfaces re-evaluate so the video unmutes and
// the audio tracks start playing.
//
// Audio should "just work" by default: digital-signage environments routinely
// relax the autoplay policy (kiosk Chrome launched with
// --autoplay-policy=no-user-gesture-required, an installed PWA, or the Android
// WebView with the media-gesture requirement disabled). On mount we therefore
// make an OPTIMISTIC unlock attempt — play a silent clip unmuted; if the
// browser allows it we flip `unlocked` straight away and sound starts with no
// tap. If the browser rejects it (standard desktop Chrome/Safari), we silently
// fall back to the gesture path below. Either way the operator's explicit
// screen/video mute still wins — this hook only governs *when* permitted audio
// is allowed to begin, never *whether* a muted surface plays sound.
//
// Two tricky details Chrome enforces and Firefox loosely follows:
//
// 1. requestFullscreen consumes the "transient activation" Chrome uses to
//    authorize unmuted media. If the FullscreenPrompt click fires the
//    React handler first (which calls requestFullscreen), our follow-up
//    React effect that flips video.muted = false runs without activation
//    and Chrome silently keeps the audio output off. We bind in the
//    CAPTURE phase so we run before any other click handler.
//
// 2. Even with capture-phase ordering, React effects run on a microtask
//    after the click handler returns. Once the activation is consumed by
//    requestFullscreen, the effect's video.muted = false yields no audio.
//    We work around this by priming a silent <audio> SYNCHRONOUSLY inside
//    the gesture handler — Chrome marks the page as "audio engaged" for
//    the session, and subsequent muted=false flips produce sound.

import { useEffect, useState } from 'react'

const UNLOCK_EVENTS = [
  'pointerdown',
  'click',
  'touchstart',
  'keydown',
  'fullscreenchange',
] as const

// 1-sample silent WAV (RIFF header + 1 sample of PCM zero). Played
// synchronously on first gesture to mark the page as audio-engaged so
// later play() / muted=false calls aren't blocked by Chrome's policy.
const SILENT_WAV =
  'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA='

// Module-level state shared by every hook caller. Listeners attach once
// per page lifetime so multiple subscribers don't double-fire the prime.
let isUnlocked = false
let listenersBound = false
const subscribers = new Set<(v: boolean) => void>()

function primeAudio() {
  try {
    const a = new Audio(SILENT_WAV)
    a.muted = false
    const p = a.play()
    if (p && typeof p.then === 'function') {
      p.catch(() => { /* Chrome still blocking — best effort */ })
    }
  } catch {
    /* ignore */
  }
}

// Flip the shared flag and fan the change out to every React subscriber.
// Idempotent — repeated calls after the first unlock are no-ops.
function markUnlocked() {
  if (isUnlocked) return
  isUnlocked = true
  subscribers.forEach(fn => {
    try { fn(true) } catch { /* ignore */ }
  })
}

// Optimistic, gesture-free unlock. On relaxed-autoplay surfaces (kiosk Chrome,
// installed PWA, Android WebView) the silent clip plays unmuted with no user
// activation, so we mark the page unlocked and audio surfaces start sound
// immediately. On stock browsers the play() promise rejects and we do nothing
// — the gesture handler below remains the recovery path.
function tryProactiveUnlock() {
  if (isUnlocked) return
  try {
    const a = new Audio(SILENT_WAV)
    a.muted = false
    const p = a.play()
    if (p && typeof p.then === 'function') {
      p.then(markUnlocked).catch(() => { /* blocked — wait for a retry / gesture */ })
    }
  } catch {
    /* ignore */
  }
}

// Schedule of retry delays (ms) for the proactive unlock. A single attempt on
// mount can lose the race when the audio subsystem isn't ready yet, when the
// signage tab starts hidden/backgrounded, or before the surface's autoplay
// permission has settled. Re-attempting over the first ~10s makes gesture-free
// audio reliable on relaxed-autoplay deployments (kiosk Chrome / installed PWA)
// without ever waiting for a tap. Each attempt is a no-op once unlocked.
const PROACTIVE_RETRY_MS = [400, 1200, 3000, 6000, 10000]

function scheduleProactiveUnlock() {
  tryProactiveUnlock()
  PROACTIVE_RETRY_MS.forEach(delay => {
    window.setTimeout(() => { if (!isUnlocked) tryProactiveUnlock() }, delay)
  })
}

function bindListenersOnce() {
  if (listenersBound) return
  listenersBound = true
  const handler = () => {
    if (!isUnlocked) {
      // Prime SYNCHRONOUSLY so Chrome treats it as gesture-initiated, then
      // notify subscribers — the React re-render and follow-up effects run
      // on a microtask, by which point the silent <audio> has already
      // cleared the autoplay quota.
      primeAudio()
      markUnlocked()
    }
  }
  // Capture phase so we run before the FullscreenPrompt click handler
  // (which consumes activation via requestFullscreen).
  UNLOCK_EVENTS.forEach(e =>
    document.addEventListener(e, handler, { capture: true, passive: true } as AddEventListenerOptions),
  )
  // A signage screen often boots hidden (launcher swaps tabs, HDMI input not
  // selected yet); the autoplay attempt only counts once the page is visible,
  // so re-try whenever it becomes visible until we're unlocked.
  document.addEventListener('visibilitychange', () => {
    if (!isUnlocked && document.visibilityState === 'visible') tryProactiveUnlock()
  }, { passive: true })
  // Try to unlock without waiting for a tap — succeeds wherever the autoplay
  // policy is relaxed, which is the common signage deployment. Retried over the
  // first few seconds so a single early miss doesn't leave audio silent.
  scheduleProactiveUnlock()
}

export function useAudioUnlock(): boolean {
  const [unlocked, setUnlocked] = useState(isUnlocked)

  useEffect(() => {
    bindListenersOnce()
    subscribers.add(setUnlocked)
    // Catch up if a gesture already happened before this component mounted
    // (e.g. the FullscreenPrompt fired before ContentDisplay rendered).
    if (isUnlocked) setUnlocked(true)
    return () => { subscribers.delete(setUnlocked) }
  }, [])

  return unlocked
}
